
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ChevronDown } from "lucide-react";
// import "./LoginPage.css";
import googlesymbol from "../../Images/googlesymbol.png";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user"); // Default to "user"
  const [toast, setToast] = useState({ message: "", type: "", visible: false });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleEmailChange = (e) => setEmail(e.target.value);
  const handlePasswordChange = (e) => setPassword(e.target.value);
  const handleRoleChange = (e) => setRole(e.target.value);
  const togglePasswordVisibility = () => setShowPassword(!showPassword)

  const showToast = (message, type) => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: "", type: "", visible: false }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !role) {
      showToast("Please fill all fields.", "error");
      return;
    }

    let endpoint = "";
    let redirectPath = "/dashboard";

    // Determine endpoint and redirect path based on selected role
    switch (role) {
      case "user":
        endpoint = `${process.env.REACT_APP_SERVER_API}/api/login`;
        redirectPath = "/dashboard";
        break;
      case "admin":
        endpoint = `${process.env.REACT_APP_SERVER_API}/api/login`;
        redirectPath = "/admin-dashboard";
        break;
      case "executive":
        endpoint = `${process.env.REACT_APP_SERVER_API}/api/executive/login`;
        redirectPath = "/executive-dashboard";
        break;
      default:
        showToast("Invalid role selected.", "error");
        return;
    }

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();

      if (response.ok) {
        const userRole = data.user.role; // Use backend-assigned role
        showToast("Login successful!", "success");

        // Set localStorage based on backend response
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("role", userRole);

        console.log("localStorage after login:", {
          user: localStorage.getItem("user"),
          isLoggedIn: localStorage.getItem("isLoggedIn"),
          role: localStorage.getItem("role"),
        });

        // Redirect based on backend role (extra safety)
        if (userRole === "admin") {
          redirectPath = "/admin-dashboard";
        } else if (userRole === "executive") {
          redirectPath = "/executive-dashboard";
        } else {
          redirectPath = "/dashboard";
        }

        setTimeout(() => navigate(redirectPath), 1000);
      } else {
        showToast(data.error || "Login failed.", "error");
      }
    } catch (error) {
      console.error("Error during login:", error);
      showToast("An error occurred. Please try again.", "error");
    }
  };

  const handleGoogleSignIn = () => {
    console.log("Continue with Google");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        {/* Brand Title */}
        <h2 className="text-3xl font-bold text-center mb-2">
          <span className="text-blue-600">C</span>allCentri Login
        </h2>
        <h4 className="text-gray-600 text-center mb-8">
          Login to Your Account
        </h4>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Input */}
          <div>
            <input
              type="email"
              id="email"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email"
            />
          </div>

          {/* Password Input with Eye Icon */}
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Role Dropdown */}
          <div className="relative">
            <select
              id="role"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all appearance-none bg-white cursor-pointer"
              value={role}
              onChange={handleRoleChange}
            >
              <option value="user">User</option>
              <option value="executive">Executive</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none w-5 h-5" />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Login
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center my-6">
          <div className="flex-grow h-px bg-gray-300"></div>
          <span className="px-3 text-gray-500 text-sm">OR</span>
          <div className="flex-grow h-px bg-gray-300"></div>
        </div>

        {/* Google Sign-in Button */}
        <button
          onClick={handleGoogleSignIn}
          className="w-full flex items-center justify-center bg-white border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <img src={googlesymbol} alt="Google" className="w-9 h-5" />
          Continue with Google
        </button>
      </div>

      {/* Toast Notification */}
      {toast.visible && (
        <div
          className={`fixed top-20 left-1/2 -translate-x-1/2 px-6 py-3 rounded-lg text-white font-medium animate-fade-in-out ${toast.type === "success" ? "bg-green-500" : "bg-red-500"
            }`}
        >
          {toast.message}
        </div>
      )}

      {/* Custom Animation for Toast */}
      <style jsx global>{`
        @keyframes fade-in-out {
          0% { opacity: 0; transform: translateY(-10px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-10px); }
        }
        .animate-fade-in-out {
          animation: fade-in-out 3s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;